import { handleActions } from 'redux-actions';
import { SubmissionError, reset, getFormValues } from 'redux-form';
import { LOCATION_CHANGE } from 'react-router-redux';
import qs from 'qs';
import moment from 'moment';

import http from '../../shared/http';
import endpoints from '../../../config/endpoints';
import intl from '../../shared/intl';
import { actions as rootActions } from '../../root';

import _pluck from 'lodash/fp/pluck';
import _cloneDeep from 'lodash/cloneDeep';
import _path from 'lodash/fp/path';
import _pathOr from 'lodash/fp/pathOr';
import _compose from 'lodash/fp/compose';
import _identity from 'lodash/fp/identity';
import _forEach from 'lodash/forEach';
import _pick from 'lodash/fp/pick';

import checkPermissions from '../../shared/check-permissions';
import { default as createCalendar, getDateRangeFromCalendar } from '../../shared/calendar';
import { isEUCountry } from '../../../../../shared/js/helpers';

const calendar = createCalendar({
	namespace: 'applicantProfile',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment()
			.subtract(1, 'month')
			.format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const APPLY_FILTERS = 'applicant-profile/apply-filters';
const FETCH_DATA_SUCCESS = 'applicant-profile/fetch-data-success';
const FETCH_DATA_ERROR = 'applicant-profile/fetch-data-error';
const SHOW_MORE_LOGS_SUCCESS = 'applicant-profile/show-more-logs-success';
const REFETCH_LOGS_SUCCESS = 'applicant-profile/refetch-logs-success';
const COMMENT_SUCCESS = 'applicant-profile/comment-success';
const COMMENT_ERROR = 'applicant-profile/comment-error';
const TOGGLE_PROGRESS_BAR = 'applicant-profile/toggle-progress-bar';
const SHOW_COMMENT_SUBMIT_BTN = 'applicant-profile/show-comment-submit-btn';
const REQUEST_ERROR = 'applicant-profile/request-error';
const RESET_CALENDAR = 'applicant-profile/reset-calendar';
const CHANGE_TAB = 'applicant-profile/change-tab';
const CLEAR_OFFSET = 'applicant-profile/clear-offset';
const OPEN_MODAL = 'applicant-profile/open-modal';
const CLOSE_MODAL = 'applicant-profile/close-modal';
const REGISTRATION_SUCCESS = 'applicant-profile/registration-success';
const REGISTRATION_FAILED = 'applicant-profile/registration-failed';

const LIMIT_ON_REGISTRATION = 3;

const checkCanRegister = applicant => applicant
	&& process.env.CPA_ENV === 'everad'
	&& !applicant.affiliate_id
	&& !applicant.reg_key
	&& applicant.reg_counter < LIMIT_ON_REGISTRATION
	&& applicant.extended_location
	&& !isEUCountry(applicant.extended_location.country_code)
	&& applicant.email
	&& (applicant.nickname && applicant.nickname.length > 2)
	&& (
		(applicant.skype && applicant.skype.length > 2) ||
		(applicant.telegram && applicant.telegram.length > 2)
	);

export const allowedPermissions = {
	view: 'affiliates_list',
	edit: 'affiliates_update',
	register: 'register_affiliate'
};

const initialState = {
	data: null,
	logsPagination: {
		currentPage: 1,
		perPage: 10,
		total: null
	},
	applicantId: null,
	currentTab: 'timeline',
	search: null,
	showProgressBar: false,
	showCommentSubmitBtn: false,
	error: false,
	isOpenRegistrationModal: false,
	calendar: calendar.initialState
};

export const actions = Object.assign({

	fetchData: props => (dispatch, getState) => {
		const { perPage } = getState().applicantProfile.logsPagination;
		const query = qs.stringify({
			offset: 0,
			limit: perPage,
			date_range: getDateRangeFromCalendar(calendar)
		});
		const id = _pathOr(props, ['params', 'id'])(props);
		return http({
			url: [
				`${endpoints.APPLICANTS.BASE}/${id}`,
				`${endpoints.APPLICANTS.LOGS.replace('{id}', id)}?${query}`
			],
			method: 'get',
			actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR],
			addToResponse: id
		})(dispatch);
	},

	changeTab: tab => (dispatch, getState) => {
		const { currentTab } = getState().applicantProfile;

		dispatch({
			type: CHANGE_TAB,
			payload: tab
		});

		if (tab === 'timeline' && currentTab !== 'timeline') {
			dispatch(actions.apply());
		}
	},

	openModal: () => ({ type: OPEN_MODAL }),

	closeModal: () => ({ type: CLOSE_MODAL }),

	registerAffiliate: data => (dispatch, getState) => {
		dispatch(actions.toggleSpinner());
		return http({
			url: `${endpoints.APPLICANTS.REGISTRATION}/${data.id}`,
			method: 'post',
			actions: [REGISTRATION_SUCCESS, REGISTRATION_FAILED]
		})(dispatch)
			.then(action => {
				if (action.type === REGISTRATION_SUCCESS) {
					dispatch(actions.fetchData(data.id));
					dispatch(actions.toggleSpinner());
				}
			});
	},

	showMoreLogs: () => (dispatch, getState) => {
		const { applicantId, logsPagination, calendar } = getState().applicantProfile;
		const { perPage, currentPage } = logsPagination;
		const offset = currentPage * perPage;
		const page = currentPage + 1;

		const query = qs.stringify({
			offset, limit: perPage,
			date_range: getDateRangeFromCalendar(calendar)
		});

		return http({
			url: `${endpoints.APPLICANTS.LOGS.replace('{id}', applicantId)}?${query}`,
			method: 'get',
			actions: [SHOW_MORE_LOGS_SUCCESS, FETCH_DATA_ERROR],
			addToResponse: page
		})(dispatch);
	},

	toggleSpinner: () => dispatch => dispatch(rootActions.spinnerToggle()),

	toggleProgressBar: () => ({ type: TOGGLE_PROGRESS_BAR }),

	refetchLogs: () => (dispatch, getState) => {
		const state = getState();
		const { applicantId, logsPagination, calendar } = state.applicantProfile;
		const { perPage } = logsPagination;
		const searchString = _compose(
			_pathOr('', ['search']),
			getFormValues('applicantProfileSearchForm')
		)(state);

		const query = qs.stringify({
			search: searchString,
			offset: 0,
			limit: perPage,
			date_range: getDateRangeFromCalendar(calendar)
		});

		dispatch({ type: CLEAR_OFFSET });

		return http({
			url: `${endpoints.APPLICANTS.LOGS.replace('{id}', applicantId)}?${query}`,
			method: 'get',
			actions: [REFETCH_LOGS_SUCCESS, FETCH_DATA_ERROR]
		})(dispatch)
			.then(action => {
				if (action.type === REFETCH_LOGS_SUCCESS) {
					dispatch(actions.toggleSpinner());
					return action;
				}
			});
	},

	submitComment: data => (dispatch, getState) => {
		const state = getState();
		const { applicantId } = state.applicantProfile;

		dispatch(actions.toggleSpinner());

		return http({
			url: `${endpoints.APPLICANTS.LOGS.replace('{id}', applicantId)}/comment`,
			method: 'post',
			data: { comment: data.comment || ''},
			actions: [COMMENT_SUCCESS, COMMENT_ERROR]
		})(dispatch)
			.then(action => {
				if (action.type === COMMENT_SUCCESS) {
					dispatch(actions.resetCalendar());
					dispatch(reset('applicantProfileCommentForm'));
					dispatch(reset('applicantProfileSearchForm'));
					return dispatch(actions.refetchLogs());
				}
				if (action.type === COMMENT_ERROR && action.payload.response.status === 400) {
					dispatch(actions.toggleSpinner());
					const validationsErrors = {};
					const responseErrors = action.payload.response.data;

					_forEach(responseErrors, value => {
						const key = value.dataPath.slice(1);
						validationsErrors[key] = value.message;
					});

					throw new SubmissionError(validationsErrors);
				}
			});
	},

	setShowCommentSubmitBtn: () => ({ type: SHOW_COMMENT_SUBMIT_BTN }),

	apply: () => (dispatch, setState) => {
		dispatch(actions.toggleSpinner());
		dispatch(actions.refetchLogs());
	},

	clear: () => dispatch => {
		dispatch(actions.resetCalendar());
		dispatch(reset('applicantProfileSearchForm'));
		dispatch(reset('applicantProfileCommentForm'));
		return dispatch(actions.refetchLogs());
	},

	resetCalendar: () => ({ type: RESET_CALENDAR })

},
calendar.actions
);

const reducers = Object.assign({

	[LOCATION_CHANGE]: (state, action) => {
		if (action.payload.pathname.indexOf('applicants/profile') !== -1) {
			return {
				...state
			};
		}
		return initialState;
	},
	[APPLY_FILTERS]: (state, action) => {
		return {
			...state,
			data: {
				...state.data,
				logs: action.payload
			}
		};
	},
	[OPEN_MODAL]: state => ({ ...state, isOpenRegistrationModal: true }),
	[CLOSE_MODAL]: state => ({ ...state, isOpenRegistrationModal: false }),
	[FETCH_DATA_SUCCESS]: (state, action) => {
		const [applicant, logs] = _pluck('data')(action.payload);
		return {
			...state,
			data: { ...applicant, logs: logs.rows },
			applicantId: applicant.id,
			logsPagination: {
				...state.logsPagination,
				total: logs.total
			},
			showProgressBar: false,
			error: false
		};
	},
	[SHOW_MORE_LOGS_SUCCESS]: (state, action) => {
		return {
			...state,
			data: { ...state.data, logs: state.data.logs.concat(action.payload.data.rows) },
			logsPagination: {
				...state.logsPagination,
				currentPage: action.meta
			}
		};
	},
	[SHOW_COMMENT_SUBMIT_BTN]: state => ({
		...state, showCommentSubmitBtn: true
	}),
	[REFETCH_LOGS_SUCCESS]: (state, action) => ({
		...state,
		data: { ...state.data, logs: action.payload.data.rows },
		logsPagination: {
			...state.logsPagination,
			total: action.payload.data.total
		},
		showCommentSubmitBtn: false
	}),
	[CLEAR_OFFSET]: state => ({
		...state,
		logsPagination: {
			...state.logsPagination,
			offset: 0,
			currentPage: 1
		}
	}),
	[RESET_CALENDAR]: state => ({
		...state,
		calendar: calendar.initialState
	}),
	[FETCH_DATA_ERROR]: state => ({ ...state, error: true, showProgressBar: false }),
	[REQUEST_ERROR]: state => ({ ...state, error: true, showProgressBar: false }),
	[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
	[CHANGE_TAB]: (state, action) => ({
		...state,
		currentTab: action.payload
	})

},
calendar.reducers
);

export default handleActions(reducers, _cloneDeep(initialState));

export const selectors = {
	getData: state => state.applicantProfile.data,
	getLogsPagination: state => state.applicantProfile.logsPagination,
	getCan: state => checkPermissions(state, allowedPermissions),
	getIntl: state => intl(state),
	getProgressBar: state => state.applicantProfile.showProgressBar,
	getError: state => state.applicantProfile.error,
	getCurrentTab: state => state.applicantProfile.currentTab,
	getSearchInitialValue: state => ({ search: state.applicantProfile.search }),
	getIsModalOpen: state => state.applicantProfile.isOpenRegistrationModal,
	getCommentSubmitBtn: state => state.applicantProfile.showCommentSubmitBtn,
	getRegistrationInitialValues: _compose(
		_pick(['email', 'birthday', 'skype', 'telegram', 'extended_location', 'id', 'nickname', 'phone']),
		_pathOr({}, ['applicantProfile', 'data'])
	),
	getCalendar: state => calendar.selectors.getCalendar(
		state => state.applicantProfile.calendar,
		_identity
	)(state),
	getAffiliateFilterQuery: state => {
		const affiliate = _path(['applicantProfile', 'data', 'extended_affiliate'])(state);
		if (!affiliate) return '';

		const query = {
			filters: { affiliates: [affiliate.id] }
		};

		const entity = {
			filters: [{
				name: 'affiliates',
				values: [{ value: affiliate.id, label: affiliate.email }]
			}]
		};

		return qs.stringify({ query, entity});
	},
	getCanRegister: _compose(
		Boolean,
		checkCanRegister,
		_pathOr({}, ['applicantProfile', 'data'])
	)

};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/applicants/profile/index.js